import * as Yup from 'yup';

import { CHECKBOX_TERMS_NAME } from '../constants';

const passwordValidationMessage =
  'Password must be at least 8 characters long, contain one upper case letter, and one symbol';

export const passwordSchema = Yup.string()
  .min(8, passwordValidationMessage)
  .matches(/[A-Z]/, passwordValidationMessage)
  .matches(/[.!@?#"$%&:';()*+,\-=[\]^_{|}<>~±§`/\\]/, passwordValidationMessage)
  .required(passwordValidationMessage);

const unicodeVariants: { [key: string]: string } = {
  ᴍ: 'm',
  ᴀ: 'a',
  ᴅ: 'd',
  ᴘ: 'p',
  ᴡ: 'w',
  s: 's',
  ᵐ: 'm',
  ᵃ: 'a',
  ᵈ: 'd',
  ᵖ: 'p',
  ʷ: 'w',
  ˢ: 's',
  ₘ: 'm',
  ₐ: 'a',
  ₚ: 'p',
  ₛ: 's',
  '𝒎': 'm',
  '𝒶': 'a',
  '𝒹': 'd',
  '𝓅': 'p',
  '𝓌': 'w',
  '𝓈': 's',
  '𝓶': 'm',
  '𝓪': 'a',
  '𝓭': 'd',
  '𝓹': 'p',
  '𝔀': 'w',
  '𝔰': 's',
  '𝐦': 'm',
  '𝐚': 'a',
  '𝐝': 'd',
  '𝐩': 'p',
  '𝐰': 'w',
  '𝐬': 's',
  '𝑚': 'm',
  '𝑎': 'a',
  '𝑑': 'd',
  '𝑝': 'p',
  '𝑤': 'w',
  '𝑠': 's',
  '𝗆': 'm',
  '𝖺': 'a',
  '𝖽': 'd',
  '𝗉': 'p',
  '𝗐': 'w',
  '𝗌': 's',
  '𝔪': 'm',
  '𝔞': 'a',
  '𝔡': 'd',
  '𝔭': 'p',
  '𝔴': 'w',
  '𝗠': 'm',
  '𝗔': 'a',
  '𝗗': 'd',
  '𝗣': 'p',
  '𝗪': 'w',
  '𝗦': 's',
  ä: 'a',
  ö: 'o',
  ü: 'u',
  é: 'e',
  è: 'e',
  ê: 'e',
  á: 'a',
  à: 'a',
  â: 'a',
  ç: 'c',
  ñ: 'n',
  ï: 'i',
};

const normalizeText = (text: string): string =>
  [...text] // Handles Unicode surrogate pairs correctly
    .map((char) => unicodeVariants[char] || char)
    .join('')
    .normalize('NFD') // Normalize diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritic marks
    .toLowerCase(); // Convert to lowercase

const cleanInput = (input: string): string => {
  if (!input) {
    return '';
  }
  return normalizeText(input)
    .replace(/[^a-z\s]/g, '') // Remove special characters (keep only letters and spaces)
    .replace(/\s+/g, ' ') // Convert multiple spaces to a single space
    .trim(); // Trim leading/trailing spaces
};

const containsBlockedWords = (input: string): boolean => {
  if (!input) {
    return false;
  }

  const normalizedInput = cleanInput(input);

  if (/^\s*mp\s*$/i.test(normalizedInput)) {
    return true;
  } // Reject "mp" alone
  if (/\bmad\s?paws\b|\bmadapaws\b/i.test(normalizedInput)) {
    return true;
  } // Reject "mad paws" or "madapaws"

  return false;
};

export const emailSignUpSchema = Yup.object().shape({
  checkboxOptions: Yup.array()
    .of(Yup.string().required())
    .test('required-validation', 'Please accept terms and conditions', (value) => {
      if (!value || !Array.isArray(value)) {
        return false;
      }
      return value.includes(CHECKBOX_TERMS_NAME);
    }),
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Please enter a valid email address'),
  firstName: Yup.string()
    .required('Please enter your first name')
    .test('no-mad-paws-or-mp', 'Invalid name, please try again.', (value) => {
      if (!value) {
        return true;
      } // Allow empty values (handled by required)
      return !containsBlockedWords(value);
    }),
  lastName: Yup.string()
    .required('Please enter your last name')
    .test('no-mad-paws-or-mp', 'Invalid name, please try again.', (value) => {
      if (!value) {
        return true;
      } // Allow empty values (handled by required)
      return !containsBlockedWords(value);
    }),
  password: passwordSchema,
});
